import { useGetRecoveryConfigQuery } from 'api/recoveryAi'
import useFlag from 'hooks/useFlag'
// Deviates from the no-scene-boundary-imports rule, for the same reason
// components/Navbar/OperationsCenter already does: recoveryStatus is a
// dependency-free predicate over the api/recoveryAi config this hook already
// fetches, so re-deriving it here would fork the definition of "Recovery is
// active" away from the one Landing's own gate uses.
import { isRecoveryActive } from 'scenes/RecoveryAI/recoveryStatus'
import { useAbility } from 'utilities/ability'

/**
 * "RecoveryAI is live for this merchant AND this viewer can reach it" — the
 * single condition every surface that links INTO /recovery_ai/* should branch
 * on. All three parts are required:
 *
 *  - the `recovery-ai` LD flag: RecoveryAI's own router redirects to "/" when
 *    it is off (scenes/RecoveryAI/index.tsx), so a link is a dead end without it.
 *  - an ACTIVE config: below that, Landing serves the static PromoLanding
 *    marketing page instead of the tab bar, so a link to a tab lands on
 *    marketing copy rather than the tab.
 *  - the ability ProtectedRoute itself resolves for the /recovery_ai route —
 *    `access` under RBAC, `manage` on the legacy path (see ProtectedRoute's
 *    resolvedAccessType) — so the link can't point at a page that bounces the
 *    viewer straight back out.
 *
 * The config request is skipped unless the first and third hold, so a merchant
 * without RecoveryAI (or a viewer without access to it) issues no RAI request
 * just by loading a page that consults this hook.
 */
const useRecoveryAiOn = (): boolean => {
  const recoveryAiFlag = useFlag('recovery-ai', false)
  const rbacEnabled = useFlag('role-based-access-control', false)

  const ability = useAbility()
  const canAccessRecoveryAi = ability.can(
    rbacEnabled ? 'access' : 'manage',
    'MerchantSettings'
  )

  const { data: config } = useGetRecoveryConfigQuery(undefined, {
    skip: !(recoveryAiFlag && canAccessRecoveryAi),
  })

  return recoveryAiFlag && canAccessRecoveryAi && isRecoveryActive(config)
}

export default useRecoveryAiOn
